import { useEffect, useState } from 'react';
import { adminApi } from '../lib/api.ts';
import { Btn } from '../components/Btn.tsx';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { formatActivity, relativeTime } from '../lib/activityFormat.ts';
import type { ActivityEvent } from '../lib/types.ts';

export function AdminActivity() {
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [cursor, setCursor] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [hasMore, setHasMore] = useState(false);

  async function load(beforeId: number | null) {
    try {
      const res = await adminApi.get<{ events: ActivityEvent[]; next_cursor: number | null }>(
        '/api/activity',
        { query: { limit: 50, before_id: beforeId ?? undefined } },
      );
      setEvents((prev) => (beforeId === null ? res.events : [...prev, ...res.events]));
      setCursor(res.next_cursor);
      setHasMore(res.next_cursor !== null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }

  useEffect(() => {
    void load(null);
  }, []);

  if (loading) return <CenteredSpinner />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Activity log</h1>
        <p className="mt-1 text-sm text-slate-500">Audit trail of every state change.</p>
      </div>
      <ErrorMessage error={error} />
      {events.length === 0 ? (
        <EmptyState title="No activity yet" />
      ) : (
        <div className="space-y-2">
          {events.map((e) => {
            const { title, detail, actor } = formatActivity(e);
            return (
              <div key={e.id} className="rounded-lg border border-slate-200 bg-white p-3 text-sm">
                <div className="flex items-start justify-between gap-3">
                  <span className="font-medium text-slate-900">{title}</span>
                  <span
                    className="shrink-0 text-xs text-slate-400"
                    title={new Date(e.occurred_at).toLocaleString()}
                  >
                    {relativeTime(e.occurred_at)}
                  </span>
                </div>
                {detail && <div className="mt-1 text-sm text-slate-600">{detail}</div>}
                <div className="mt-1 text-xs text-slate-500">
                  by {actor}
                  {e.company_name && e.company_name !== actor ? ` · ${e.company_name}` : ''}
                  <span className="ml-1 text-slate-400">· {e.event_type}</span>
                </div>
              </div>
            );
          })}
          {hasMore && (
            <div className="pt-3 text-center">
              <Btn
                variant="secondary"
                loading={loadingMore}
                onClick={() => {
                  setLoadingMore(true);
                  void load(cursor);
                }}
              >
                Load more
              </Btn>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
